import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, CircularProgress } from "@mui/material";
import { Link } from "react-router-dom";
import GoogleAddressTextField from "../../common/GoogleAddressTextField";
import CountrySelectField from "../../common/dashboard/CountrySelectField";
import { googleAddressToMemberFields, SelectedGoogleAddress } from "../../../utils/googleAddressAutocomplete";
import {
  CmpProspectNoteReturn,
  CmpProspectStaffOptionReturn,
} from "../../../graphql/types";
import { RoutePaths } from "../../../enumStrings";
import UnsavedChangesPrompt from "../../common/UnsavedChangesPrompt";
import { hasUnsavedFormChanges } from "../../common/formStateDirty";
import { provinceOptions } from "../../common/utilities/ProvincePicker";
import { formatStaffPhoneInput } from "../../../utils/memberContactFormatting";
import InvoiceSearchableSelect from "../Invoicing/InvoiceSearchableSelect";
import { InvoiceDesignScope } from "../Invoicing/InvoiceDesignSystem";
import { ProspectNotesSection } from "./ProspectNotesSection";
import {
  LeadCaptureSelect,
  ProspectLeadCaptureOption,
} from "./LeadCaptureSelect";
import {
  emptyProspectFormValues,
  getProspectErrorMessage,
  getProspectFieldLengthMessage,
  PROSPECT_FIELD_MAX_LENGTHS,
  ProspectFieldsInput,
  ProspectFormErrors,
  ProspectFormValues,
  ProspectSaveResult,
  toProspectFieldsInput,
  validateProspectForm,
} from "./prospectFormModel";
import { buildStaffErrorFallback } from "../../../utils/userFacingError";
import {
  prospectCardClassName,
  prospectDesignFontFamily,
  prospectGoldButtonSx,
  prospectInputClassName,
  prospectNoOptionsSx,
  prospectOutlinedButtonSx,
  prospectSelectMenuSx,
  prospectSelectSx,
} from "./prospectDesign";

export type ProspectFormAction = "save" | "convert";
export type ProspectFormSaveInput = {
  data: ProspectFieldsInput;
  initialNoteComment?: string;
};

type ProspectFormProps = {
  mode: "create" | "update" | "converted";
  initialValues: ProspectFormValues;
  leadCaptures: readonly ProspectLeadCaptureOption[];
  leadCapturesLoading?: boolean;
  leadCapturesError?: string;
  staffOptions: readonly CmpProspectStaffOptionReturn[];
  staffOptionsLoading?: boolean;
  staffOptionsError?: string;
  notes?: readonly CmpProspectNoteReturn[];
  notesLoading?: boolean;
  notesError?: string;
  currentStaffDisplayName: string;
  onCreateLeadCapture: (label: string) => Promise<ProspectLeadCaptureOption>;
  onSave: (input: ProspectFormSaveInput) => Promise<ProspectSaveResult>;
  onSaved?: (
    prospect: ProspectSaveResult,
    action: ProspectFormAction,
  ) => void | Promise<void>;
  onAddNote?: (comment: string) => Promise<void>;
  viewMemberPath?: string;
  conversionSummary?: React.ReactNode;
};

type ProspectTextFieldProps = {
  field: keyof ProspectFormValues;
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (field: keyof ProspectFormValues, value: string) => void;
  error?: string;
  required?: boolean;
  disabled: boolean;
  frozen: boolean;
  type?: "text" | "email" | "tel" | "url";
  layout?: "subgrid" | "stack";
  onAddressSelect?: (address: SelectedGoogleAddress) => void;
};

const ProspectTextField = ({
  field,
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
  required = false,
  disabled,
  frozen,
  type = "text",
  layout = "subgrid",
  onAddressSelect,
}: ProspectTextFieldProps) => (
  <div
    className={
      layout === "stack"
        ? "flex min-w-0 flex-col gap-2"
        : "grid min-w-0 grid-rows-subgrid row-span-3 content-start gap-y-2"
    }
    data-prospect-field-error={error ? "true" : undefined}
  >
    <label
      htmlFor={id}
      className="text-[14px] font-semibold leading-[normal] text-[#0A0A0A]"
    >
      {label}
      {required ? <span className="text-[#E24C4C]"> *</span> : null}
    </label>
    {onAddressSelect ? <GoogleAddressTextField
      id={id} value={value} placeholder={placeholder} disabled={disabled && !frozen}
      onValueChange={(next) => onChange(field, next)} onAddressSelect={onAddressSelect}
      error={Boolean(error)} nativeInputProps={{
        type, required, readOnly: frozen, className: prospectInputClassName,
        "aria-describedby": error ? id + "-error" : undefined,
        style: { borderColor: error ? "#E24C4C" : undefined,
          backgroundColor: frozen ? "#F7F7F8" : undefined, color: frozen ? "#9E9E9E" : undefined },
      }}
    /> : field === "country" ? <CountrySelectField
      id={id} value={value} disabled={disabled && !frozen} error={Boolean(error)}
      onValueChange={(next) => onChange(field, next)} nativeInputProps={{
        required, readOnly: frozen, className: prospectInputClassName,
        "aria-describedby": error ? id + "-error" : undefined,
        style: { borderColor: error ? "#E24C4C" : undefined,
          backgroundColor: frozen ? "#F7F7F8" : undefined, color: frozen ? "#9E9E9E" : undefined },
      }}
    /> : <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      required={required}
      disabled={disabled && !frozen}
      readOnly={frozen}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? id + "-error" : undefined}
      className={prospectInputClassName}
      style={{
        borderColor: error ? "#E24C4C" : undefined,
        backgroundColor: frozen ? "#F7F7F8" : undefined,
        color: frozen ? "#9E9E9E" : undefined,
      }}
      onChange={(event) =>
        onChange(
          field,
          field === "mainPhoneNumber" || field === "mobilePhoneNumber"
            ? formatStaffPhoneInput(event.target.value)
            : event.target.value,
        )
      }
    />}
    {error ? (
      <p id={id + "-error"} className="text-[12px] leading-[normal] text-[#E24C4C]">
        {error}
      </p>
    ) : layout === "subgrid" ? (
      <span />
    ) : null}
  </div>
);

export const PROSPECT_NOTE_REQUIRED_MESSAGE =
  "Conversation Notes and Comments is required.";

const scrollToFirstError = () => {
  window.setTimeout(() => {
    document
      .querySelector('[data-prospect-field-error="true"]')
      ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, 60);
};

export const ProspectForm = ({
  mode,
  initialValues,
  leadCaptures,
  leadCapturesLoading = false,
  leadCapturesError,
  staffOptions,
  staffOptionsLoading = false,
  staffOptionsError,
  notes = [],
  notesLoading = false,
  notesError,
  currentStaffDisplayName,
  onCreateLeadCapture,
  onSave,
  onSaved,
  onAddNote,
  viewMemberPath,
  conversionSummary,
}: ProspectFormProps) => {
  const [values, setValues] = useState<ProspectFormValues>(initialValues);
  const [savedValues, setSavedValues] =
    useState<ProspectFormValues>(initialValues);
  const lastSeenInitialValuesRef = useRef<ProspectFormValues>({
    ...initialValues,
  });
  const lastAppliedInitialValuesRef = useRef<ProspectFormValues>({
    ...initialValues,
  });
  const [errors, setErrors] = useState<ProspectFormErrors>({});
  const [footerErrorMessage, setFooterErrorMessage] = useState("");
  const [pendingAction, setPendingAction] = useState<ProspectFormAction | null>(
    null,
  );
  const [noteComment, setNoteComment] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [commentsSaved, setCommentsSaved] = useState(false);
  const [noteSaveError, setNoteSaveError] = useState("");
  const frozen = mode === "converted";
  const formValuesAreDirty = useMemo(
    () => hasUnsavedFormChanges({ baseline: savedValues, current: values }),
    [savedValues, values],
  );
  const isDirty = !frozen && (formValuesAreDirty || noteComment.length > 0);
  const isSaving = pendingAction !== null;

  useEffect(() => {
    if (
      hasUnsavedFormChanges({
        baseline: lastSeenInitialValuesRef.current,
        current: initialValues,
      })
    ) {
      lastSeenInitialValuesRef.current = { ...initialValues };
    }
    if (
      isDirty ||
      !hasUnsavedFormChanges({
        baseline: lastAppliedInitialValuesRef.current,
        current: lastSeenInitialValuesRef.current,
      })
    ) {
      return;
    }
    const nextValues = { ...lastSeenInitialValuesRef.current };
    lastAppliedInitialValuesRef.current = nextValues;
    setValues(nextValues);
    setSavedValues(nextValues);
    setErrors({});
  }, [initialValues, isDirty]);

  const handleFieldChange = (
    field: keyof ProspectFormValues,
    value: string,
  ) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFooterErrorMessage("");
  };

  const handleNoteSave = async () => {
    const comment = noteComment.trim();
    if (!comment || frozen || noteSaving || isSaving) return false;
    if (comment.length > PROSPECT_FIELD_MAX_LENGTHS.pendingNoteComment) {
      setNoteSaveError(
        getProspectFieldLengthMessage(
          "Conversation Notes and Comments",
          PROSPECT_FIELD_MAX_LENGTHS.pendingNoteComment,
        ),
      );
      return false;
    }
    setNoteSaving(true);
    setNoteSaveError("");
    try {
      if (mode === "create") {
        handleFieldChange("pendingNoteComment", comment);
      } else {
        if (!onAddNote) throw new Error("The note could not be saved.");
        await onAddNote(comment);
      }
      setNoteComment("");
      setCommentsSaved(true);
      return true;
    } catch (error) {
      setNoteSaveError(
        // The note saves before the prospect, so a failed note stops the page save.
        getProspectErrorMessage(error, buildStaffErrorFallback("save your note", "Your other changes were not saved either.")),
      );
      return false;
    } finally {
      setNoteSaving(false);
    }
  };

  const saveProspect = async (
    action: ProspectFormAction,
    valuesToSave = values,
    navigateAfterSave = true,
  ) => {
    setPendingAction(action);
    try {
      const savedProspect = await onSave({
        data: toProspectFieldsInput(valuesToSave),
        initialNoteComment:
          mode === "create"
            ? valuesToSave.pendingNoteComment || undefined
            : undefined,
      });
      // Do not let a stale query response replace the confirmed mutation.
      lastAppliedInitialValuesRef.current = {
        ...lastSeenInitialValuesRef.current,
      };
      const savedSnapshot =
        mode === "create" ? { ...emptyProspectFormValues } : { ...valuesToSave };
      setValues(savedSnapshot);
      setSavedValues(savedSnapshot);
      setNoteComment("");
      setCommentsSaved(false);
      setNoteSaveError("");
      if (navigateAfterSave) await onSaved?.(savedProspect, action);
      return true;
    } catch (error) {
      setFooterErrorMessage(
        getProspectErrorMessage(error, buildStaffErrorFallback("save the prospect")),
      );
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  // CMP Dashboard (15) D:15012-15017 (Monday D24): Conversation Notes and
  // Comments is required on Create and Update Prospect (not once converted),
  // and the note saves with the page action. Convert to Member does not ask
  // for a note (design onConvertToMember) but still saves a typed one.
  const valuesWithTypedNote = () =>
    mode === "create" && noteComment.trim()
      ? {
          ...values,
          pendingNoteComment: [values.pendingNoteComment, noteComment.trim()]
            .filter(Boolean).join("\n\n"),
        }
      : values;
  const prospectNoteMissing = () =>
    !noteComment.trim() &&
    !(mode === "create" && values.pendingNoteComment.trim());

  const saveWithNote = async (
    action: ProspectFormAction,
    navigateAfterSave: boolean,
  ) => {
    const valuesToSave = valuesWithTypedNote();
    const nextErrors = validateProspectForm(valuesToSave);
    const noteMissing = action === "save" && prospectNoteMissing();
    setErrors(nextErrors);
    setNoteSaveError(noteMissing ? PROSPECT_NOTE_REQUIRED_MESSAGE : "");
    setFooterErrorMessage("");
    if (Object.keys(nextErrors).length > 0 || noteMissing) {
      // The approved prospect-only exception keeps validation at its field.
      scrollToFirstError();
      return false;
    }
    if (mode === "update" && noteComment.trim() && !(await handleNoteSave())) {
      return false;
    }
    return saveProspect(action, valuesToSave, navigateAfterSave);
  };

  const handleSubmit = async (action: ProspectFormAction) => {
    if (frozen || isSaving || noteSaving) return;
    await saveWithNote(action, true);
  };

  const staffNames = useMemo(() => {
    const names = staffOptions
      .map(({ firstName, lastName }) => (firstName + " " + lastName).trim())
      .filter(Boolean);
    if (values.createdByName && !names.includes(values.createdByName))
      names.push(values.createdByName);
    return Array.from(new Set(names));
  }, [staffOptions, values.createdByName]);

  const renderField = (
    field: keyof ProspectFormValues,
    id: string,
    label: string,
    placeholder: string,
    required = false,
    type: ProspectTextFieldProps["type"] = "text",
    layout: ProspectTextFieldProps["layout"] = "subgrid",
  ) => (
    <ProspectTextField
      key={field}
      field={field}
      id={id}
      label={label}
      placeholder={placeholder}
      value={values[field]}
      error={errors[field]}
      required={required}
      disabled={isSaving || noteSaving}
      frozen={frozen}
      type={type}
      layout={layout}
      onChange={handleFieldChange}
      onAddressSelect={field === "streetAddress" ? (address) => {
        if (frozen || isSaving || noteSaving) return;
        const selected = googleAddressToMemberFields(address);
        setValues(current => ({ ...current, streetAddress: selected.streetAddress,
          city: selected.city, province: address.provinceCode || address.province,
          postalCode: selected.postalOrZipCode, country: selected.country }));
        setErrors(current => {
          const next = { ...current };
          for (const key of ["streetAddress", "city", "province", "postalCode", "country"] as const) delete next[key];
          return next;
        });
        setFooterErrorMessage("");
      } : undefined}
    />
  );

  return (
    <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
      <form
        noValidate
        className="flex flex-col gap-6 pb-10 pt-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit("save");
        }}
      >
        <UnsavedChangesPrompt
          variant="page"
          when={isDirty}
          saving={isSaving || noteSaving}
        />
        {leadCapturesError ? (
          <Alert severity="error">{leadCapturesError}</Alert>
        ) : null}
        {staffOptionsError ? (
          <Alert severity="error">{staffOptionsError}</Alert>
        ) : null}
        <div className="flex w-full max-w-[720px] flex-col gap-[22px]">
          <div
            data-prospect-field-error={
              errors.leadCaptureId ? "true" : undefined
            }
          >
            <LeadCaptureSelect
              value={values.leadCaptureId}
              onChange={(value) => handleFieldChange("leadCaptureId", value)}
              options={leadCaptures}
              onCreateLeadCapture={onCreateLeadCapture}
              error={errors.leadCaptureId}
              loading={leadCapturesLoading}
              disabled={frozen || isSaving || noteSaving}
            />
          </div>
          <div
            data-prospect-field-error={
              errors.createdByName ? "true" : undefined
            }
          >
            <label
              id="prospect-created-by-label"
              htmlFor="prospect-created-by"
              className="mb-2 block text-[15px] font-semibold leading-[normal] text-[#0A0A0A]"
            >
              Created By
            </label>
            <InvoiceSearchableSelect<string>
              id="prospect-created-by"
              labelledById="prospect-created-by-label"
              value={values.createdByName || null}
              options={staffNames}
              getOptionKey={(name) => name}
              getOptionLabel={(name) => name}
              onChange={(value) =>
                handleFieldChange("createdByName", value || "")
              }
              placeholder="Select Staff Name"
              searchPlaceholder="Search Staff Name"
              noOptionsText="No matches found"
              noOptionsSx={prospectNoOptionsSx}
              menuSx={prospectSelectMenuSx}
              sx={prospectSelectSx}
              optionRowHeight={40}
              outlinedArrow
              loading={staffOptionsLoading}
              disabled={frozen || isSaving || noteSaving}
              error={!!errors.createdByName}
              helperText={errors.createdByName}
            />
          </div>
        </div>

        {conversionSummary}
        <section
          className={prospectCardClassName}
          aria-labelledby="prospect-company-information"
        >
          <h2
            id="prospect-company-information"
            className="text-[18px] font-semibold leading-[normal] text-[#0A0A0A]"
          >
            Company Information
          </h2>
          <div className="grid grid-cols-1 grid-rows-[auto_auto_auto] gap-5 md:grid-cols-2">
            {renderField(
              "companyName",
              "prospect-company-name",
              "Company Name",
              "Enter Company Name",
              true,
            )}
            {renderField(
              "companyWebsite",
              "prospect-company-website",
              "Company Website",
              "Enter Company Website URL",
              false,
              "url",
            )}
          </div>
          <div className="grid grid-cols-1 grid-rows-[auto_auto_auto] gap-5 md:grid-cols-2">
            {renderField(
              "mainContactName",
              "prospect-main-contact-name",
              "Primary Contact Name",
              "Enter Primary Contact Name",
              true,
            )}
            {renderField(
              "mainContactTitle",
              "prospect-main-contact-title",
              "Title",
              "Enter Contact Person’s Title",
            )}
          </div>
          <div className="grid grid-cols-1 grid-rows-[auto_auto_auto] gap-5 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_90px_minmax(0,1fr)]">
            {renderField(
              "mainContactEmail",
              "prospect-main-contact-email",
              "Contact Email",
              "Enter Primary Contact Email",
              true,
              "email",
            )}
            {renderField(
              "mainPhoneNumber",
              "prospect-main-phone",
              "Contact Number",
              "Enter Contact Number",
              false,
              "tel",
            )}
            {renderField(
              "mainPhoneNumberExtension",
              "prospect-main-phone-extension",
              "Ext.",
              "0000",
              false,
              "tel",
            )}
            {renderField(
              "mobilePhoneNumber",
              "prospect-mobile-phone",
              "Mobile Phone Number",
              "Enter Mobile Phone Number",
              false,
              "tel",
            )}
          </div>
        </section>
        <section
          className={prospectCardClassName}
          aria-labelledby="prospect-company-address"
        >
          <h2
            id="prospect-company-address"
            className="text-[18px] font-semibold leading-[normal] text-[#0A0A0A]"
          >
            Company Address
          </h2>
          {renderField(
            "streetAddress",
            "prospect-street-address",
            "Street Address",
            "Enter Street Address, Example: 123 Maple E.g.",
            false,
            "text",
            "stack",
          )}
          <div className="grid grid-cols-1 grid-rows-[auto_auto_auto] gap-5 md:grid-cols-2">
            {renderField("city", "prospect-city", "City", "Enter City Name")}
            <div
              className="grid min-w-0 grid-rows-subgrid row-span-3 gap-y-2"
              data-prospect-field-error={errors.province ? "true" : undefined}
            >
              <label
                id="prospect-province-label"
                htmlFor="prospect-province"
                className="text-[14px] font-semibold leading-[normal] text-[#0A0A0A]"
              >
                Province/State
              </label>
              <InvoiceSearchableSelect
                id="prospect-province"
                labelledById="prospect-province-label"
                options={provinceOptions}
                value={
                  provinceOptions.find(
                    (option) => option.value === values.province,
                  ) || null
                }
                getOptionKey={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(option) =>
                  handleFieldChange("province", option?.value || "")
                }
                placeholder="Select Province/State"
                searchPlaceholder="Search Province/State"
                noOptionsText="No matches found"
                noOptionsSx={prospectNoOptionsSx}
                menuSx={prospectSelectMenuSx}
                sx={prospectSelectSx}
                optionRowHeight={40}
                outlinedArrow
                disabled={frozen || isSaving || noteSaving}
                error={!!errors.province}
                helperText={errors.province}
              />
              <span />
            </div>
            {renderField(
              "postalCode",
              "prospect-postal-code",
              "Postal/Zip Code",
              "Enter the Postal/Zip Code",
            )}
            {renderField(
              "country",
              "prospect-country",
              "Country",
              "Select Country",
            )}
          </div>
        </section>
        <ProspectNotesSection
          mode={mode}
          staffOptions={staffOptions}
          notes={notes}
          notesLoading={notesLoading}
          notesError={notesError}
          comment={noteComment}
          onCommentChange={(comment) => {
            setNoteComment(comment);
            setCommentsSaved(false);
            setNoteSaveError("");
          }}
          isSaving={noteSaving || isSaving}
          commentsSaved={commentsSaved}
          saveError={noteSaveError}
        />
        <div className="pt-1">
          {footerErrorMessage ? (
            <Alert severity="error" className="mb-4">
              {footerErrorMessage}
            </Alert>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-3">
            {frozen ? (
              <>
                <Button
                  component={Link}
                  to={RoutePaths.chamberAdmin_prospects_manage}
                  sx={prospectOutlinedButtonSx}
                >
                  Back to Manage Prospects
                </Button>
                <span
                  className="flex self-stretch"
                  title={
                    viewMemberPath
                      ? undefined
                      : "This prospect is not linked to a member, so View Member is not available. Find the member in Members > Manage Members."
                  }
                >
                  <Button
                    component={viewMemberPath ? Link : "button"}
                    to={viewMemberPath}
                    disabled={!viewMemberPath}
                    sx={prospectGoldButtonSx}
                  >
                    View Member
                  </Button>
                </span>
              </>
            ) : (
              <>
                <Button
                  type="submit"
                  disabled={isSaving || noteSaving}
                  sx={prospectGoldButtonSx}
                  startIcon={
                    pendingAction === "save" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  Save
                </Button>
                {mode === "update" ? (
                  <Button
                    type="button"
                    disabled={isSaving || noteSaving}
                    sx={prospectGoldButtonSx}
                    onClick={() => void handleSubmit("convert")}
                    startIcon={
                      pendingAction === "convert" ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    Convert to Member
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </form>
    </InvoiceDesignScope>
  );
};
